import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FileText, MessageCircle, Search } from 'lucide-react'
import { doc, onSnapshot } from 'firebase/firestore'
import {
  Channel,
  ChannelList,
  Chat,
  ComponentProvider,
  MessageList,
  MessageComposer,
  Window,
  useChatContext,
  useChannelStateContext,
  type ChannelListItemUIProps,
  type EmptyStateIndicatorProps,
} from 'stream-chat-react'
import type { Channel as StreamChannel, ChannelFilters, ChannelSort } from 'stream-chat'
import 'stream-chat-react/dist/css/index.css'
import { NameRevealModal } from '@/components/chat/NameRevealModal'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useStreamChat } from '@/hooks/use-stream-chat'
import {
  channelHasUserMessages,
  formatChannelListTime,
  getChannelActivityDate,
  getChannelAddress,
  getChannelBiddleRoute,
  getChannelPreviewText,
  getChatUserRole,
  getConversationDisplayName,
  getNameRevealSubtitle,
  getPeerDisplayName,
  isNameRevealSubtitleTappable,
  parseChatDoc,
  toChatHash,
  toStreamChannelId,
  type ChatDocState,
} from '@/lib/chat'
import { formatAddressDisplay, splitAddressDisplay } from '@/lib/address'
import { openBiddleDetail } from '@/lib/biddle-navigation'
import { db } from '@/lib/firebase'
import { cn } from '@/lib/utils'

const OPENED_CHANNELS_KEY = 'biddle.openedChatChannels'

function readOpenedChannels(): Set<string> {
  try {
    const raw = localStorage.getItem(OPENED_CHANNELS_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

function persistOpenedChannels(ids: Set<string>) {
  localStorage.setItem(OPENED_CHANNELS_KEY, JSON.stringify([...ids]))
}

export function ChatPage() {
  return (
    <RequireAuth>
      <ChatInner />
    </RequireAuth>
  )
}

function ActiveChannelSync({
  channelId,
  onOpened,
}: {
  channelId?: string
  onOpened: (id: string) => void
}) {
  const { channel, setActiveChannel } = useChatContext()
  const navigate = useNavigate()
  const { client } = useStreamChat()

  useEffect(() => {
    if (!channelId || !client) return
    const id = toStreamChannelId(channelId)
    if (channel?.id === id) return
    const ch = client.channel('messaging', id)
    void ch.watch().then(() => setActiveChannel(ch))
  }, [channelId, client, channel?.id, setActiveChannel])

  useEffect(() => {
    if (channel?.id && channelId && channel.id !== toStreamChannelId(channelId)) {
      navigate(`/chat/${channel.id}`, { replace: true })
    }
  }, [channel?.id, channelId, navigate])

  useEffect(() => {
    if (channel?.id) onOpened(channel.id)
  }, [channel?.id, onOpened])

  return null
}

function BiddleChannelListEmpty({ listType }: EmptyStateIndicatorProps) {
  if (listType !== 'channel') return null
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <MessageCircle className="mb-3 h-12 w-12 text-biddle-blue" strokeWidth={1.5} />
      <p className="text-base font-semibold text-navy">No Messages</p>
      <p className="mt-1 text-sm text-muted">Your messages will appear here.</p>
    </div>
  )
}

function BiddleMessageListEmpty({ listType }: EmptyStateIndicatorProps) {
  if (listType !== 'message') return null
  // iOS leaves the thread blank; keep a quiet desktop/mobile prompt without Stream’s icon chrome.
  return (
    <div className="flex h-full min-h-[12rem] flex-1 items-center justify-center px-6 py-12">
      <p className="text-sm text-muted">Send a message to start the conversation</p>
    </div>
  )
}

function BiddleChannelListItemUI({
  props,
  openedChannels,
}: {
  props: ChannelListItemUIProps
  openedChannels: Set<string>
}) {
  const { channel, setActiveChannel, active, unread, onSelect } = props
  const { streamId } = useStreamChat()
  const navigate = useNavigate()
  const peer = getPeerDisplayName(channel, streamId)
  const address = formatAddressDisplay(getChannelAddress(channel))
  const unreadCount = unread ?? 0
  const preview = getChannelPreviewText(channel)
  const activityAt = getChannelActivityDate(channel)
  const timeLabel = formatChannelListTime(activityAt)
  const isNewUnopened =
    !channelHasUserMessages(channel) && !openedChannels.has(channel.id ?? '')
  const emphasize = unreadCount > 0 || isNewUnopened
  const image =
    typeof (channel.data as { image?: unknown } | undefined)?.image === 'string'
      ? (channel.data as { image: string }).image
      : undefined
  const memberCount = Object.keys(channel.state?.members ?? {}).length
  const showDmDot = memberCount > 0 && memberCount <= 2

  return (
    <button
      type="button"
      onClick={(e) => {
        onSelect?.(e)
        setActiveChannel?.(channel)
        navigate(`/chat/${channel.id}`)
      }}
      className={cn(
        'flex w-full min-h-[68px] items-center gap-3 border-b border-border/80 px-4 py-3 text-left transition hover:bg-surface',
        active && 'bg-biddle-blue/10',
      )}
    >
      <div className="relative h-12 w-12 shrink-0">
        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-surface">
          {image ? (
            <img src={image} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-biddle-blue">{peer.slice(0, 1)}</span>
          )}
        </div>
        {showDmDot ? (
          <span
            className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-success"
            aria-hidden
          />
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p
            className={cn(
              'truncate text-[15px] text-navy',
              emphasize ? 'font-semibold' : 'font-normal',
            )}
          >
            {peer}
          </p>
          {timeLabel ? (
            <span className="shrink-0 text-xs text-muted">{timeLabel}</span>
          ) : null}
        </div>
        <p
          className={cn(
            'mt-0.5 truncate text-sm',
            emphasize ? 'font-medium text-navy' : 'text-muted',
          )}
        >
          {preview}
        </p>
        <p
          className={cn(
            'mt-0.5 truncate text-[11px] text-biddle-blue',
            emphasize && 'font-medium',
          )}
        >
          {address}
        </p>
      </div>

      {unreadCount > 0 ? (
        <span className="shrink-0 rounded-full bg-biddle-blue px-1.5 py-0.5 text-[11px] font-semibold text-white">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}
    </button>
  )
}

function BiddleChannelHeader({ onOpenNameReveal }: { onOpenNameReveal: () => void }) {
  const { channel } = useChannelStateContext()
  const { streamId } = useStreamChat()
  const navigate = useNavigate()
  const [docState, setDocState] = useState<ChatDocState | null>(null)

  const chatHash = channel?.id ? toChatHash(channel.id) : ''
  const role = getChatUserRole(channel, streamId)
  const title = getConversationDisplayName(channel, streamId, docState)
  const subtitle = getNameRevealSubtitle(role, docState)
  const subtitleTappable = isNameRevealSubtitleTappable(role, docState)
  const address = formatAddressDisplay(getChannelAddress(channel))
  const { street, locality } = splitAddressDisplay(address)
  const biddleRoute = getChannelBiddleRoute(channel)
  const image =
    typeof (channel?.data as { image?: unknown } | undefined)?.image === 'string'
      ? (channel?.data as { image: string }).image
      : undefined

  useEffect(() => {
    if (!chatHash) {
      setDocState(null)
      return
    }
    const unsub = onSnapshot(
      doc(db, 'chats', chatHash),
      (snap) => {
        setDocState(parseChatDoc(snap.data() as Record<string, unknown> | undefined))
      },
      () => setDocState(null),
    )
    return () => unsub()
  }, [chatHash])

  return (
    <div className="shrink-0 border-b border-border bg-white">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          className="rounded-lg p-2 text-muted transition hover:bg-surface md:hidden"
          onClick={() => navigate('/chat')}
          aria-label="Back to messages"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="min-w-0 flex-1 text-center md:text-left">
          <p className="truncate text-base font-semibold text-navy">{title}</p>
          {subtitle ? (
            subtitleTappable ? (
              <button
                type="button"
                onClick={onOpenNameReveal}
                className="mt-0.5 inline-block truncate text-xs font-semibold text-biddle-blue"
              >
                {subtitle}
              </button>
            ) : (
              <p className="mt-0.5 truncate text-xs text-muted">{subtitle}</p>
            )
          ) : null}
        </div>

        {image ? (
          <div className="hidden h-10 w-10 shrink-0 overflow-hidden rounded-full md:block">
            <img src={image} alt="" className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="hidden h-10 w-10 shrink-0 md:block" aria-hidden />
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-border/70 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-navy">{street}</p>
          {locality ? <p className="truncate text-xs text-muted">{locality}</p> : null}
        </div>
        {biddleRoute ? (
          <button
            type="button"
            onClick={() =>
              openBiddleDetail(navigate, biddleRoute.propertyId, biddleRoute.biddleId)
            }
            className="inline-flex shrink-0 items-center gap-1.5 rounded-2xl border border-biddle-blue/30 bg-biddle-blue/10 px-3 py-1.5 text-xs font-medium text-biddle-blue transition hover:bg-biddle-blue/15"
          >
            <FileText className="h-3.5 w-3.5" />
            Biddle details
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ChatInner() {
  const { channelId: rawChannelId } = useParams<{ channelId?: string }>()
  const channelId = rawChannelId ? toStreamChannelId(rawChannelId) : undefined
  const [searchParams, setSearchParams] = useSearchParams()
  const { client, streamId, apiKey, isReady, isConnecting, error, reconnect } = useStreamChat()
  const [search, setSearch] = useState('')
  const [openedChannels, setOpenedChannels] = useState<Set<string>>(() => readOpenedChannels())
  const [nameRevealOpen, setNameRevealOpen] = useState(false)

  const markOpened = useCallback((id: string) => {
    setOpenedChannels((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      persistOpenedChannels(next)
      return next
    })
  }, [])

  // Deep link from /name-reveal/:id → /chat/:id?names=1
  useEffect(() => {
    if (searchParams.get('names') === '1' && channelId) {
      setNameRevealOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('names')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, channelId, setSearchParams])

  const filters: ChannelFilters = useMemo(
    () => ({
      type: 'messaging',
      members: { $in: streamId ? [streamId] : ['__none__'] },
    }),
    [streamId],
  )

  const sort: ChannelSort = useMemo(() => ({ last_message_at: -1 }), [])

  const channelRenderFilterFn = useCallback(
    (channels: StreamChannel[]) => {
      const q = search.trim().toLowerCase()
      if (!q) return channels
      return channels.filter((ch) => {
        const peer = getPeerDisplayName(ch, streamId).toLowerCase()
        const address = getChannelAddress(ch).toLowerCase()
        const preview = getChannelPreviewText(ch).toLowerCase()
        return peer.includes(q) || address.includes(q) || preview.includes(q)
      })
    },
    [search, streamId],
  )

  const ListItemUI = useCallback(
    (itemProps: ChannelListItemUIProps) => (
      <BiddleChannelListItemUI props={itemProps} openedChannels={openedChannels} />
    ),
    [openedChannels],
  )

  if (!apiKey) {
    return (
      <EmptyState
        title="Chat unavailable"
        description="Stream Chat isn’t configured for this environment (missing VITE_STREAM_API_KEY)."
      />
    )
  }

  if (error && !isReady) {
    return (
      <EmptyState
        title="Couldn’t connect to chat"
        description={error}
        actionLabel="Retry"
        onAction={() => void reconnect()}
      />
    )
  }

  if (isConnecting || !isReady || !client || !streamId) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
        <Spinner />
        <p className="text-sm text-muted">Connecting to chat…</p>
      </div>
    )
  }

  const showThread = Boolean(channelId)

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 overflow-hidden rounded-2xl border border-border bg-white md:grid-cols-[340px_1fr]">
        <Chat client={client}>
          <ComponentProvider
            value={{
              ChannelListHeader: () => null,
              ChannelListItemUI: ListItemUI,
              EmptyStateIndicator: (emptyProps) =>
                emptyProps.listType === 'channel' ? (
                  <BiddleChannelListEmpty {...emptyProps} />
                ) : (
                  <BiddleMessageListEmpty {...emptyProps} />
                ),
            }}
          >
            <ActiveChannelSync channelId={channelId} onOpened={markOpened} />
            <div
              className={cn(
                'flex h-full min-h-0 flex-col border-b border-border md:border-b-0 md:border-r',
                showThread && 'hidden md:flex',
              )}
            >
              <div className="shrink-0 space-y-3 border-b border-border px-4 py-3">
                <h1 className="text-xl font-bold text-navy">Messages</h1>
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    className="w-full rounded-xl border border-border bg-surface py-2 pl-9 pr-3 text-sm text-navy outline-none ring-biddle-blue/30 placeholder:text-muted focus:ring-2"
                  />
                </label>
              </div>
              <div className="relative min-h-0 flex-1 overflow-hidden">
                <ChannelList
                  filters={filters}
                  sort={sort}
                  options={{ state: true, presence: true, limit: 30 }}
                  customActiveChannel={channelId}
                  setActiveChannelOnMount={!channelId}
                  channelRenderFilterFn={channelRenderFilterFn}
                  EmptyStateIndicator={BiddleChannelListEmpty}
                />
              </div>
            </div>
            <div
              className={cn(
                'flex min-h-0 flex-col overflow-hidden',
                !showThread && 'hidden md:flex',
              )}
            >
              {channelId ? (
                <Channel>
                  <Window>
                    <BiddleChannelHeader onOpenNameReveal={() => setNameRevealOpen(true)} />
                    <MessageList />
                    <MessageComposer />
                  </Window>
                </Channel>
              ) : (
                <div className="hidden flex-1 items-center justify-center p-8 text-center md:flex">
                  <div>
                    <MessageCircle
                      className="mx-auto mb-3 h-12 w-12 text-biddle-blue"
                      strokeWidth={1.5}
                    />
                    <p className="font-semibold text-navy">Select a conversation</p>
                    <p className="mt-1 text-sm text-muted">
                      Messages appear here after an owner chooses to chat on a Biddle.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </ComponentProvider>
        </Chat>
      </div>

      {channelId ? (
        <NameRevealModal
          channelId={channelId}
          open={nameRevealOpen}
          onClose={() => setNameRevealOpen(false)}
        />
      ) : null}
    </div>
  )
}
